/**
 * Client helper to retry a v2 training that failed with an unknown
 * error (GPU crash / hang / heartbeat timeout). Posts to
 * `POST /api/capture/retry`.
 *
 * `retrainAsDryWet` is the "retrain anyway" opt-in for an INCONSISTENT
 * failure: the route only honors it for rows that failed with the
 * INCONSISTENT error, and it flips the training's `type` to `dry-wet`
 * before re-firing. The sweep-v3 input (the canonical T3K sweep) becomes
 * the dry signal, so the same audio retrains without the V3 self-ESR
 * consistency check that rejected it. The resulting model may be poor
 * quality.
 *
 * Callers MUST `router.refresh()` on success: live polling
 * (`TrainingsProvider`) is gated on SSR data that says a training is
 * active, so the reset row won't be polled again until SSR re-runs.
 */
export async function retryTraining(
  trainingId: number,
  options?: { retrainAsDryWet?: boolean }
): Promise<{ ok: boolean; error?: string }> {
  try {
    const res = await fetch('/api/capture/retry', {
      method: 'POST',
      body: JSON.stringify({
        trainingId,
        ...(options?.retrainAsDryWet ? { retrainAsDryWet: true } : {}),
      }),
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      return { ok: false, error: data?.error ?? 'Failed to retry training' };
    }
    return { ok: true };
  } catch (err: any) {
    return { ok: false, error: err?.message ?? 'Failed to retry training' };
  }
}
